import { NavLink, Outlet } from 'react-router-dom';
import { Heart, Home, MapPin, Package, UserRound } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { usePlatform } from '@/providers/PlatformProvider';
import type { FeatureKey } from '@/types/platform';

/**
 * The signed-in account area — BUILD-PLAN §16, §58.
 *
 * One layout wrapping every account screen, so the customer keeps a fixed
 * frame of reference: the same rail in the same place, with the current
 * section marked. Without it each screen is an island and the only way
 * between them is the browser's back button.
 *
 * The rail is a rail on desktop and a horizontal scroller on small screens —
 * not a hamburger. These are five destinations a customer moves between
 * constantly while managing an account; hiding them behind a tap would add a
 * step to every single one.
 */
interface AccountLink {
  to: string;
  label: string;
  icon: LucideIcon;
  /** `end` so the dashboard is not marked active on every child route. */
  end?: boolean;
  feature?: FeatureKey;
}

const LINKS: AccountLink[] = [
  { to: '/account', label: 'Overview', icon: Home, end: true },
  { to: '/orders', label: 'Orders', icon: Package, feature: 'orders' },
  { to: '/account/addresses', label: 'Addresses', icon: MapPin },
  { to: '/account/profile', label: 'Profile', icon: UserRound },
  { to: '/wishlist', label: 'Wishlist', icon: Heart, feature: 'wishlist' },
];

export function AccountLayout() {
  const { isVisible } = usePlatform();

  const links = LINKS.filter((link) => !link.feature || isVisible(link.feature));

  return (
    <div className="mx-auto max-w-6xl px-5 py-10 sm:px-6 lg:px-8 lg:py-14">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] lg:gap-12">
        <nav aria-label="Account" className="min-w-0 lg:pt-2">
          {/*
            Scrolls sideways on small screens rather than wrapping to three
            ragged rows.

            No negative-margin bleed here. `-mx-5` made the rail 40px wider
            than its own grid column, and nothing above it clipped that, so
            the whole DOCUMENT scrolled sideways on a phone — the rail got its
            edge-to-edge look by breaking the page. Scrolling inside the
            content box costs a little inset and keeps the page still.
          */}
          <ul className="no-scrollbar flex gap-2 overflow-x-auto lg:flex-col lg:gap-1">
            {links.map(({ to, label, icon: Icon, end }) => (
              <li key={to} className="shrink-0">
                <NavLink
                  to={to}
                  end={end ?? false}
                  className={({ isActive }) =>
                    [
                      'flex min-h-11 items-center gap-2.5 rounded-sm px-3 text-sm tracking-wide transition-colors',
                      isActive
                        ? 'bg-accent/10 text-accent'
                        : 'text-muted hover:bg-surface-muted hover:text-ink',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className="h-4 w-4 shrink-0"
                        strokeWidth={isActive ? 2 : 1.5}
                        aria-hidden="true"
                      />
                      <span className="whitespace-nowrap">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
